import {
  Box, Card, CardHeader, FormControl, InputLabel, MenuItem,
  Pagination, Select, Stack, Typography
} from '@mui/material';
import {
  DataGrid, GridCellEditStopReasons,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../api/axiosClient';
import CustomAvatar from '../../components/mui/avatar';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import { getInitials } from '../../utils/get-initials';
import MemberToolbar from './MemberToolbar';

const renderClient = params => {
  const { row } = params
  const stateNum = Math.floor(Math.random() * 6)
  const states = ['success', 'error', 'warning', 'info', 'primary', 'secondary']
  const color = states[stateNum]

  return (
    <CustomAvatar skin='light' color={color} sx={{ mr: 3, fontSize: '.8rem', width: '1.875rem', height: '1.875rem' }}>
      {getInitials(row.username ? row.username : 'User')}
    </CustomAvatar>
  )
}

const TableMember = props => {
  const { filters, submit, selected, setSelected, page, setPage, pageSize, setPageSize } = props;
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('desc');
  const [list, setList] = useState([]);
  const [sortColumn, setSortColumn] = useState('regtime');

  const loadColumnMemberTable = window.localStorage.getItem('columnMemberTable') ? 
      JSON.parse(window.localStorage.getItem('columnMemberTable')) : 
      {
        subscribe_date: true,
      };
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(loadColumnMemberTable);

  const loadMembers = async (sort, column) => {
    try {
      const response = await apiService.get('/members/general', {
        params: {
          ...filters,
          sort,
          column,
          page,
          pageSize
        }
      });

      setTotal(response.data.size);
      setList(response.data.list || []);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const {startDate, endDate} = filters;
        
    if(!isEmpty(startDate) && !isEmpty(endDate)) {
      loadMembers(sort, sortColumn)
    }    
  }, [page, pageSize, reload, submit, filters])

  useEffect(() => {
    window.localStorage.setItem('columnMemberTable', JSON.stringify(columnVisibilityModel) );
    setColumnVisibilityModel(columnVisibilityModel);
  }, [columnVisibilityModel])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadMembers(newModel[0].sort, newModel[0].field)
    } else {
      setSort('asc')
      setSortColumn('regtime')
    }
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  const handleStateChange = async (userid, field, newValue) => {
    setList((prevList) =>
      prevList.map((item) => {
        if (item.userid === userid) {
          return { ...item, [field]: newValue };
        }
        return item;
      })
    );
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      headerAlign: 'left'
    },
    {
      field: 'subscribe_date',
      headerName: '가입날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {moment(params.row.subscribe_date).format("YYYY-MM-DD")}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'userid',
      headerName: 'User ID',
      renderCell: params => {
        const { row } = params
  
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {renderClient(params)}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Link to={`/admin/member/${row.username}`}>
                <Typography variant='body2' sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {row.username}
                </Typography>
              </Link>
  
              <Typography variant='caption'>
                {row.email}
              </Typography>
            </Box>
          </Box>
        )
      }
    },
    {
      minWidth: 120,
      field: 'category',
      headerName: '구분*',
      hideable: false,
      renderCell: params => {  
        return (
          <Select
            value={params.row.category}
            onChange={(e) => {
              handleStateChange(params.row.userid, 'category', e.target.value);
            }}
            size="small"
            native
          >
            <option>일반</option>
            <option>임시</option>
          </Select>
        )
      }
    },
    {
      field: 'sales_manager',
      headerName: '영업당담',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.sales_manager}
        </Typography>
      )
    },
    {
      field: 'company_type',
      headerName: '업체분류',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.company_type}
        </Typography>
      )
    },
    {
      minWidth: 160,
      field: 'transaction_type',
      hideable: false,
      headerName: '거래유형*',
      renderCell: params => {  
        return (
          <Select
            value={params.row.transaction_type}
            onChange={(e) => {
              handleStateChange(params.row.userid, 'transaction_type', e.target.value);
            }}
            size="small"
            native
          >
            <option>거래고객</option>
            <option>잠재고객</option>
            <option>기타</option>
          </Select>
        )
      }
    },
    {
      minWidth: 120,
      field: 'membership_level',
      hideable: false,
      headerName: '회원등급*',
      renderCell: params => {  
        return (
          <Select
            value={params.row.membership_level}
            onChange={(e) => {
              handleStateChange(params.row.userid, 'membership_level', e.target.value);
            }}
            size="small"
            native
          >
            <option>S</option>
            <option>A</option>
            <option>B</option>
            <option>C</option>
            <option>D</option>
            <option>E</option>
            <option>F</option>
          </Select>
        )
      }
    },
    {
      minWidth: 200,
      field: 'company_name',
      headerName: '업체명',
      renderCell: params => (
        <Link to={`/admin/company/${params.row.com_id}`} >
          {params.row.company_name}
        </Link>
      )
    },
    {
      field: 'full_name',
      headerName: '성함',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.full_name}
        </Typography>
      )
    },
    {
      field: 'job_title',
      headerName: '직급',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.job_title}
        </Typography>
      )
    },
    {
      minWidth: 150,
      field: 'mobile_phone',
      headerName: '휴대폰',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.mobile_phone}
        </Typography>
      )
    },
    {
      minWidth: 200,
      field: 'email',
      headerName: '이메일',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.email}
        </Typography>
      )
    },
    {
      field: 'discount_rate',
      headerName: '할인율',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.discount_rate}%
        </Typography>
      )
    },
    {
      field: 'payment_terms',
      headerName: '결제조건',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.payment_terms}
        </Typography>
      )
    },
    {
      field: 'a_points',
      headerName: 'A포인트',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.a_points}
        </Typography>
      )
    },
    {
      field: 'total_purchase_amount',
      headerName: '누적구매금액',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.total_purchase_amount}
        </Typography>
      )
    },
    {
      field: 'purchase_count',
      headerName: '구매횟수',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.purchase_count}
        </Typography>
      )
    },
    {
      minWidth: 300,
      field: 'business_location',
      headerName: '사업자소재지',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.business_location}
        </Typography>
      )
    },
  ]

  return (
    loading ? 
    <Spinner/> :
    <Card>
      <CardHeader title='회원' />
      <DataGrid
        sx={{
          ...((list?.length || 0) == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.userid}
        pagination
        rows={list || []}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        sortingMode='server'
        paginationMode='server'
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: MemberToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            numSelected: selected?.length || 0,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = (list || []).filter((row) =>
            selectedIDs.has(row.userid),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
        onCellEditStop={(params, event) => {
          if (params.reason === GridCellEditStopReasons.enterKeyDown) {
            // event.defaultMuiPrevented = true;            
            if (event.target.value?.length === 0) {
              return
            }

            // change source data only with changed value
            setList((list || []).map(item => {
              if (item.userid == params.id) {
                  return { ...item, 
                    [params.field]: event.target.value
                  };
              }
              return item;
            }));

            // 이미 선택된 row가 수정된 경우
            setSelected(
              (list || []).filter(item => {
                if (item.userid == params.id) {
                  return { ...item, 
                    [params.field]: event.target.value
                  };
              }
              return item;
            }));

          }
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
      />
    </Card>
  )
}

export default TableMember